import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy } from 'react-feather'
import { ethers } from 'ethers'
import styled from 'styled-components'
import { useOrderBookContract } from '../../hooks/useContract'
import { useOrderBookAdminState, useAllOrdersFromApi } from '../../hooks/useOrderBook'
import { useActiveWeb3React } from '../../hooks'
import { USDT_DECIMALS, EXECUTOR_ROLE_KEY, OrderStatus } from '../../constants/orderbook'
import { ButtonSecondary } from '../../components/Button'
import { SectionTitle, InfoRow, InfoLabel, InfoValue } from './styleds'
import TransactionConfirmationModal from '../../components/TransactionConfirmationModal'
import OrderList from './OrderList'
import Popover from '../../components/Popover'
import { getEtherscanLink, shortenAddress, isAddress } from '../../utils'
import { ExternalLink } from '../../theme/components'
import { ChainId } from '@uniswap/sdk'

const Block = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.bg2};
  border-radius: 10px;
`

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

/** 参数设置区：输入与按钮各占 50% */
const ParamsFieldRow = styled(FieldRow)`
  & > * {
    flex: 1 1 50%;
    min-width: 0;
  }
  & input {
    width: 100%;
  }
`

/** 两按钮各占 50%（如救援区） */
const HalfButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  & > * {
    flex: 1 1 50%;
    min-width: 0;
  }
`

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
`

const AddressLink = styled(ExternalLink)`
  font-size: 12px;
  font-weight: 500;
`

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text2};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.bg3};
    color: ${({ theme }) => theme.text1};
  }
`

const CopyPopoverContent = styled.div`
  padding: 6px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.text1};
  white-space: nowrap;
`

/** 与成交明细中复制成功 Popover 一致 */
const QueryResultPopoverContent = styled.div`
  padding: 6px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.text1};
  white-space: nowrap;
`

/** 白名单查询按钮占 50%，Popover 的 reference 需填满此格 */
const QueryBtnWrap = styled.div`
  flex: 1 1 50%;
  min-width: 0;
  & > * {
    width: 100%;
  }
  button {
    width: 100%;
  }
`

/** 下拉框：与 SmallInput 同高，独占一行用 */
const SmallSelect = styled.select`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
  cursor: pointer;
  box-sizing: border-box;
`

const SmallInput = styled.input`
  width: 120px;
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
  box-sizing: border-box;
`

const Btn = styled(ButtonSecondary)`
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
`

interface OrderBookAdminProps {
  isAdmin: boolean
  isExecutor: boolean
  onDetail?: (orderId: number, isBuy?: boolean) => void
  onCancel?: (orderId: number) => void
  onUpdatePrice?: (orderId: number, currentPrice: string) => void
  cancellingId?: number | null
}

export default function OrderBookAdmin({ isAdmin, isExecutor, onDetail, onCancel, onUpdatePrice, cancellingId }: OrderBookAdminProps) {
  const { t } = useTranslation()
  const { chainId } = useActiveWeb3React()
  const orderBookContract = useOrderBookContract(true)
  const { paused, maxPriceMultiplier, feeReceiver, accumulatedUsdtFee, accumulatedMudFee, statistics, loading, refresh } =
    useOrderBookAdminState()

  const [txConfirmOpen, setTxConfirmOpen] = useState(false)
  const [copiedFeeReceiver, setCopiedFeeReceiver] = useState(false)
  const [txPending, setTxPending] = useState(false)
  const [txHash, setTxHash] = useState<string | undefined>(undefined)

  const sendTx = useCallback(
    async (fn: () => Promise<any>) => {
      if (!orderBookContract) return
      setTxConfirmOpen(true)
      setTxPending(true)
      setTxHash(undefined)
      try {
        const tx = await fn()
        if (tx?.hash) {
          setTxHash(tx.hash)
          setTxPending(false)
          tx.wait().then(() => refresh()).catch(() => {})
        } else setTxConfirmOpen(false)
      } catch (e) {
        console.error(e)
        setTxConfirmOpen(false)
      }
    },
    [orderBookContract, refresh]
  )

  const [minFeeRate, setMinFeeRate] = useState('1')
  const [minPrivateFeeRate, setMinPrivateFeeRate] = useState('10')
  const [minOrderValueUSDT, setMinOrderValueUSDT] = useState('10')
  const [maxOrdersPerUser, setMaxOrdersPerUser] = useState('100')
  const [maxPriceMultiplierVal, setMaxPriceMultiplierVal] = useState('5')
  const [feeReceiverAddr, setFeeReceiverAddr] = useState('')
  const [executorAddr, setExecutorAddr] = useState('')
  const [whitelistAddr, setWhitelistAddr] = useState('')
  const [whitelistStatus, setWhitelistStatus] = useState(true)
  const [whitelistQueryAddr, setWhitelistQueryAddr] = useState('')
  const [whitelistQueryResult, setWhitelistQueryResult] = useState<boolean | null>(null)
  const [whitelistQueryLoading, setWhitelistQueryLoading] = useState(false)
  const [batchCancelStart, setBatchCancelStart] = useState('0')
  const [batchCancelEnd, setBatchCancelEnd] = useState('')
  const [executorQueryAddr, setExecutorQueryAddr] = useState('')
  const [executorQueryResult, setExecutorQueryResult] = useState<boolean | null>(null)
  const [executorQueryLoading, setExecutorQueryLoading] = useState(false)

  const [allOrdersPage, setAllOrdersPage] = useState(1)
  const [allOrdersStatusFilter, setAllOrdersStatusFilter] = useState<number | undefined>(OrderStatus.OPEN)
  const [allOrdersAddressFilter, setAllOrdersAddressFilter] = useState('')
  const [allOrdersTypeFilter, setAllOrdersTypeFilter] = useState<number | undefined>(undefined)
  const allOrdersPageSize = 10
  const { orders: allOrders, total: allOrdersTotal, loading: allOrdersLoading, refresh: refreshAllOrders } = useAllOrdersFromApi(
    allOrdersPage,
    allOrdersPageSize,
    allOrdersStatusFilter,
    allOrdersAddressFilter || undefined,
    allOrdersTypeFilter
  )

  const handleAllOrdersAddressFilterChange = useCallback((v: string) => {
    setAllOrdersAddressFilter(v)
    setAllOrdersPage(1)
  }, [])

  const handleAllOrdersTypeFilterChange = useCallback((v: number | undefined) => {
    setAllOrdersTypeFilter(v)
    setAllOrdersPage(1)
  }, [])

  useEffect(() => {
    setBatchCancelEnd(String(statistics.orderCountOpen))
  }, [statistics.orderCountOpen])

  const onWhitelistQueryAddrChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setWhitelistQueryAddr(e.target.value)
    setWhitelistQueryResult(null)
  }, [])

  const handleWhitelistQuery = useCallback(async () => {
    if (!orderBookContract || !whitelistQueryAddr.trim()) return
    setWhitelistQueryLoading(true)
    setWhitelistQueryResult(null)
    try {
      const inList = await orderBookContract.whitelist(whitelistQueryAddr.trim())
      setWhitelistQueryResult(inList)
    } catch (e) {
      console.error(e)
      setWhitelistQueryResult(null)
    } finally {
      setWhitelistQueryLoading(false)
    }
  }, [orderBookContract, whitelistQueryAddr])

  useEffect(() => {
    if (whitelistQueryResult === null) return
    const t = setTimeout(() => setWhitelistQueryResult(null), 5000)
    return () => clearTimeout(t)
  }, [whitelistQueryResult])

  const handleExecutorQuery = useCallback(async () => {
    if (!orderBookContract || !executorQueryAddr.trim()) return
    setExecutorQueryLoading(true)
    setExecutorQueryResult(null)
    try {
      const has = await orderBookContract.hasRole(ethers.utils.id(EXECUTOR_ROLE_KEY), executorQueryAddr.trim())
      setExecutorQueryResult(has)
    } catch (e) {
      console.error(e)
      setExecutorQueryResult(null)
    } finally {
      setExecutorQueryLoading(false)
    }
  }, [orderBookContract, executorQueryAddr])

  useEffect(() => {
    if (executorQueryResult === null) return
    const t = setTimeout(() => setExecutorQueryResult(null), 5000)
    return () => clearTimeout(t)
  }, [executorQueryResult])

  if (loading) return <div style={{ padding: '1rem', color: 'var(--text2)' }}>{t('orderLoading')}</div>

  const trimNumber = (s: string) => (s.replace(/\.?0+$/, '') || '0')
  const formatUsdt = (raw: string) => trimNumber((Number(raw) / Math.pow(10, USDT_DECIMALS)).toFixed(2))
  const formatMud = (raw: string) => trimNumber(parseFloat(ethers.utils.formatEther(raw || '0')).toFixed(2))

  return (
    <div style={{ padding: '0 0.75rem 0.75rem' }}>
      {isAdmin && (
        <>
          <SectionTitle>{t('orderAdminState')}</SectionTitle>
          <Block>
            <InfoRow>
              <InfoLabel>{t('orderAdminPaused')}</InfoLabel>
              <InfoValue>{paused ? t('orderYes') : t('orderNo')}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminMaxPriceMultiplier')}</InfoLabel>
              <InfoValue>{maxPriceMultiplier}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminFeeReceiver')}</InfoLabel>
              <InfoValue>
                {feeReceiver && isAddress(feeReceiver) && chainId != null ? (
                  <AddressRow>
                    <AddressLink href={getEtherscanLink(chainId as ChainId, feeReceiver, 'address')} target="_blank" rel="noopener noreferrer">
                      {shortenAddress(feeReceiver)}
                    </AddressLink>
                    <Popover show={copiedFeeReceiver} content={<CopyPopoverContent>{t('orderCopySuccess')}</CopyPopoverContent>} placement="top">
                      <CopyBtn
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(feeReceiver).then(() => {
                            setCopiedFeeReceiver(true)
                            setTimeout(() => setCopiedFeeReceiver(false), 1500)
                          })
                        }}
                        title={t('orderCopyTxHash')}
                      >
                        <Copy size={14} />
                      </CopyBtn>
                    </Popover>
                  </AddressRow>
                ) : (
                  '--'
                )}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminFeeWithdrawableTotalUsdt')}</InfoLabel>
              <InfoValue>
                {formatUsdt(accumulatedUsdtFee)}/{formatUsdt(statistics.totalUsdtFeeCollected)} USDT
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminFeeWithdrawableTotalMud')}</InfoLabel>
              <InfoValue>
                {formatMud(accumulatedMudFee)}/{formatMud(statistics.totalMudFeeCollected)} MUD
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminStatOrderCountOpen')}</InfoLabel>
              <InfoValue>{statistics.orderCountOpen}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminStatOrderCountFilled')}</InfoLabel>
              <InfoValue>{statistics.orderCountFilled}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminStatOrderCountCancelled')}</InfoLabel>
              <InfoValue>{statistics.orderCountCancelled}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminStatOpenBuyUsdtTotal')}</InfoLabel>
              <InfoValue>{formatUsdt(statistics.openBuyUsdtTotal)} USDT</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminStatOpenSellMudTotal')}</InfoLabel>
              <InfoValue>{formatMud(statistics.openSellMudTotal)} MUD</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('orderAdminStatUniqueUserCount')}</InfoLabel>
              <InfoValue>{statistics.uniqueUserCount}</InfoValue>
            </InfoRow>
          </Block>
        </>
      )}

      {isAdmin && (
        <>
          <SectionTitle>{t('orderAdminPauseControl')}</SectionTitle>
          <Block>
            {paused ? (
              <Btn onClick={() => sendTx(() => orderBookContract!.unpause())}>{t('orderAdminUnpause')}</Btn>
            ) : (
              <Btn onClick={() => sendTx(() => orderBookContract!.pause())}>{t('orderAdminPause')}</Btn>
            )}
          </Block>

          <SectionTitle>{t('orderAdminWithdraw')}</SectionTitle>
          <Block>
            <Btn onClick={() => sendTx(() => orderBookContract!.withdrawFees())}>{t('orderAdminWithdrawFees')}</Btn>
          </Block>

          <SectionTitle>{t('orderAdminBatchCancelByIndex')}</SectionTitle>
          <Block>
            <InfoRow style={{ marginBottom: 8 }}>
              <InfoLabel>{t('orderAdminBatchCancelHint')}</InfoLabel>
            </InfoRow>
            <FieldRow>
              <SmallInput
                type="number"
                min={0}
                value={batchCancelStart}
                onChange={e => setBatchCancelStart(e.target.value)}
                placeholder="startIndex"
              />
              <SmallInput
                type="number"
                min={0}
                value={batchCancelEnd}
                onChange={e => setBatchCancelEnd(e.target.value)}
                placeholder="endIndex"
              />
              <Btn
                disabled={
                  statistics.orderCountOpen === 0 ||
                  (parseInt(batchCancelStart, 10) || 0) >= (parseInt(batchCancelEnd, 10) || 0)
                }
                onClick={() =>
                  sendTx(() =>
                    orderBookContract!.batchCancelOrdersByIndex(
                      parseInt(batchCancelStart, 10) || 0,
                      parseInt(batchCancelEnd, 10) || 0
                    )
                  )
                }
              >
                {t('orderAdminBatchCancelSubmit')}
              </Btn>
            </FieldRow>
          </Block>

          <SectionTitle>{t('orderAdminParams')}</SectionTitle>
          <Block>
            <ParamsFieldRow>
              <SmallInput
                type="number"
                value={minFeeRate}
                onChange={e => setMinFeeRate(e.target.value)}
                placeholder="minFeeRate"
              />
              <Btn onClick={() => sendTx(() => orderBookContract!.setMinFeeRate(minFeeRate))}>{t('orderAdminSetMinFeeRate')}</Btn>
            </ParamsFieldRow>
            <ParamsFieldRow>
              <SmallInput
                type="number"
                value={minPrivateFeeRate}
                onChange={e => setMinPrivateFeeRate(e.target.value)}
                placeholder="minPrivateFeeRate"
              />
              <Btn onClick={() => sendTx(() => orderBookContract!.setMinPrivateFeeRate(minPrivateFeeRate))}>
                {t('orderAdminSetMinPrivateFeeRate')}
              </Btn>
            </ParamsFieldRow>
            <ParamsFieldRow>
              <SmallInput
                type="number"
                value={minOrderValueUSDT}
                onChange={e => setMinOrderValueUSDT(e.target.value)}
                placeholder="minOrderValueUSDT"
              />
              <Btn
                onClick={() =>
                  sendTx(() =>
                    orderBookContract!.setMinOrderValueUSDT(ethers.utils.parseUnits(minOrderValueUSDT, USDT_DECIMALS))
                  )
                }
              >
                {t('orderAdminSetMinOrderValueUSDT')}
              </Btn>
            </ParamsFieldRow>
            <ParamsFieldRow>
              <SmallInput
                type="number"
                value={maxOrdersPerUser}
                onChange={e => setMaxOrdersPerUser(e.target.value)}
                placeholder="maxOrdersPerUser"
              />
              <Btn onClick={() => sendTx(() => orderBookContract!.setMaxOrdersPerUser(maxOrdersPerUser))}>
                {t('orderAdminSetMaxOrdersPerUser')}
              </Btn>
            </ParamsFieldRow>
            <ParamsFieldRow>
              <SmallInput
                type="number"
                value={maxPriceMultiplierVal}
                onChange={e => setMaxPriceMultiplierVal(e.target.value)}
                placeholder="maxPriceMultiplier"
              />
              <Btn onClick={() => sendTx(() => orderBookContract!.setMaxPriceMultiplier(maxPriceMultiplierVal))}>
                {t('orderAdminSetMaxPriceMultiplier')}
              </Btn>
            </ParamsFieldRow>
            <ParamsFieldRow>
              <SmallInput
                value={feeReceiverAddr}
                onChange={e => setFeeReceiverAddr(e.target.value)}
                placeholder="feeReceiver address"
              />
              <Btn onClick={() => sendTx(() => orderBookContract!.setFeeReceiver(feeReceiverAddr))}>
                {t('orderAdminSetFeeReceiver')}
              </Btn>
            </ParamsFieldRow>
          </Block>

          <SectionTitle>{t('orderAdminRescue')}</SectionTitle>
          <Block>
            <HalfButtonRow>
              <Btn onClick={() => sendTx(() => orderBookContract!.rescueExcessUSDT())}>{t('orderAdminRescueExcessUSDT')}</Btn>
              <Btn onClick={() => sendTx(() => orderBookContract!.rescueExcessMUD())}>
                {t('orderAdminRescueExcessMUD')}
              </Btn>
            </HalfButtonRow>
          </Block>

          <SectionTitle>{t('orderAdminExecutorRole')}</SectionTitle>
          <Block>
            <ParamsFieldRow>
              <SmallInput
                value={executorQueryAddr}
                onChange={e => {
                  setExecutorQueryAddr(e.target.value)
                  setExecutorQueryResult(null)
                }}
                placeholder={t('orderAdminExecutorQueryPlaceholder')}
              />
              <QueryBtnWrap>
                <Popover
                  show={executorQueryResult !== null}
                  content={
                    <QueryResultPopoverContent>
                      {executorQueryResult === true
                        ? t('orderAdminIsExecutor')
                        : executorQueryResult === false
                          ? t('orderAdminNotExecutor')
                          : ''}
                    </QueryResultPopoverContent>
                  }
                  placement="top"
                >
                  <div style={{ width: '100%' }}>
                    <Btn
                      onClick={handleExecutorQuery}
                      disabled={executorQueryLoading || !executorQueryAddr.trim()}
                    >
                      {executorQueryLoading ? t('orderLoading') : t('orderAdminExecutorQuery')}
                    </Btn>
                  </div>
                </Popover>
              </QueryBtnWrap>
            </ParamsFieldRow>
            <ParamsFieldRow style={{ marginTop: 8 }}>
              <SmallInput
                value={executorAddr}
                onChange={e => setExecutorAddr(e.target.value)}
                placeholder={t('orderAdminExecutorAddressPlaceholder')}
              />
              <Btn
                onClick={() =>
                  sendTx(() =>
                    orderBookContract!.grantRole(ethers.utils.id(EXECUTOR_ROLE_KEY), executorAddr.trim())
                  )
                }
              >
                {t('orderAdminGrantExecutor')}
              </Btn>
            </ParamsFieldRow>
          </Block>
        </>
      )}

      {(isAdmin || isExecutor) && (
        <>
          <SectionTitle>{t('orderAdminWhitelist')}</SectionTitle>
          <Block>
            <ParamsFieldRow>
              <SmallInput
                value={whitelistQueryAddr}
                onChange={onWhitelistQueryAddrChange}
                placeholder={t('orderAdminWhitelistQueryPlaceholder')}
              />
              <QueryBtnWrap>
                <Popover
                  show={whitelistQueryResult !== null}
                  content={
                    <QueryResultPopoverContent>
                      {whitelistQueryResult === true
                        ? t('orderAdminWhitelistInList')
                        : whitelistQueryResult === false
                          ? t('orderAdminWhitelistNotInList')
                          : ''}
                    </QueryResultPopoverContent>
                  }
                  placement="top"
                >
                  <div style={{ width: '100%' }}>
                    <Btn onClick={handleWhitelistQuery} disabled={whitelistQueryLoading || !whitelistQueryAddr.trim()}>
                      {whitelistQueryLoading ? t('orderLoading') : t('orderAdminWhitelistQuery')}
                    </Btn>
                  </div>
                </Popover>
              </QueryBtnWrap>
            </ParamsFieldRow>
            <FieldRow style={{ marginTop: 8 }}>
              <SmallSelect
                value={whitelistStatus ? 'add' : 'remove'}
                onChange={e => setWhitelistStatus(e.target.value === 'add')}
              >
                <option value="add">{t('orderAdminWhitelistAdd')}</option>
                <option value="remove">{t('orderAdminWhitelistRemove')}</option>
              </SmallSelect>
            </FieldRow>
            <ParamsFieldRow style={{ marginTop: 8 }}>
              <SmallInput
                value={whitelistAddr}
                onChange={e => setWhitelistAddr(e.target.value)}
                placeholder="address"
              />
              <Btn onClick={() => sendTx(() => orderBookContract!.setWhitelist(whitelistAddr, whitelistStatus))}>
                {t('orderAdminSetWhitelist')}
              </Btn>
            </ParamsFieldRow>
          </Block>
        </>
      )}

      {(isAdmin || isExecutor) && (
        <OrderList
          orders={allOrders}
          total={allOrdersTotal}
          page={allOrdersPage}
          pageSize={allOrdersPageSize}
          loading={allOrdersLoading}
          statusFilter={allOrdersStatusFilter}
          onStatusFilterChange={setAllOrdersStatusFilter}
          onPageChange={setAllOrdersPage}
          onCancel={onCancel ?? (() => {})}
          onUpdatePrice={onUpdatePrice ?? (() => {})}
          onDetail={onDetail ?? (() => {})}
          cancellingId={cancellingId ?? null}
          onRefresh={refreshAllOrders}
          listTitleKey="orderAdminAllOrders"
          showStatusFilter={true}
          showTypeFilterInRow={true}
          showAddress={true}
          showEditCancel={false}
          noOuterPadding
          showAddressFilter={true}
          addressFilter={allOrdersAddressFilter}
          onAddressFilterChange={handleAllOrdersAddressFilterChange}
          typeFilter={allOrdersTypeFilter}
          onTypeFilterChange={handleAllOrdersTypeFilterChange}
        />
      )}

      <TransactionConfirmationModal
        isOpen={txConfirmOpen}
        onDismiss={() => setTxConfirmOpen(false)}
        hash={txHash}
        attemptingTxn={txPending}
        pendingText={t('waitingForConfirmation')}
        content={() => null}
      />
    </div>
  )
}
